import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Truck, Zap, Target, Gem, Users, Send } from 'lucide-react';
import SEO from '../components/SEO';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useCountUp from '../hooks/useCountUp';
import './Home.css';

// Componente formulario de contacto rápido
const QuickContactForm = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    tipo: '',
    mensaje: '',
    honeypot: ''
  });
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState(null);
  const [formStartTime] = useState(Date.now());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Bot protection: Honeypot check
    if (formData.honeypot) {
      return;
    }
    
    // Bot protection: Minimum time to fill form (3 seconds)
    const timeToFill = Date.now() - formStartTime;
    if (timeToFill < 3000) {
      setError('Por favor, espera unos segundos antes de enviar el formulario.');
      return;
    }
    
    setEnviando(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nombre: formData.nombre,
          email: formData.email,
          asunto: `Consulta desde Home - ${formData.tipo}`,
          mensaje: formData.mensaje || `Tipo de servicio: ${formData.tipo}`,
          honeypot: formData.honeypot
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setEnviado(true);
        setFormData({ nombre: '', email: '', tipo: '', mensaje: '', honeypot: '' });
        setTimeout(() => setEnviado(false), 5000);
      } else {
        setError(data.message || 'Error al enviar el mensaje. Inténtalo de nuevo.');
      }
    } catch (err) {
      setError('Error de conexión. Por favor, inténtalo más tarde.');
    } finally {
      setEnviando(false);
    }
  };

  if (enviado) {
    return (
      <div className="hero__form hero__form--success">
        <div className="hero__form-success">
          <svg viewBox="0 0 20 20" fill="currentColor" className="hero__form-success-icon">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <h3>¡Mensaje enviado!</h3>
          <p>Te contactaremos en menos de 24 horas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hero__form">
      <h3 className="hero__form-title">Solicita información</h3>
      <p className="hero__form-subtitle">Te respondemos en menos de 24h</p>
      
      {error && (
        <div className="hero__form-error">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="hero__form-fields">
        <div className="hero__form-group">
          <input
            type="text"
            placeholder="Tu nombre"
            value={formData.nombre}
            onChange={(e) => setFormData({...formData, nombre: e.target.value})}
            required
            className="hero__form-input"
          />
        </div>
        
        <div className="hero__form-group">
          <input
            type="email"
            placeholder="Tu email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            className="hero__form-input"
          />
        </div>
        
        <div className="hero__form-group">
          <select
            value={formData.tipo}
            onChange={(e) => setFormData({...formData, tipo: e.target.value})}
            required
            className="hero__form-select"
          >
            <option value="">¿Qué servicio necesitas?</option>
            <option value="laser">Grabado Láser</option>
            <option value="3d">Impresión 3D</option>
            <option value="regalo">Regalos Personalizados</option>
            <option value="otro">Otro proyecto</option>
          </select>
        </div>
        
        <div className="hero__form-group">
          <textarea
            placeholder="Cuéntanos brevemente tu proyecto..."
            value={formData.mensaje}
            onChange={(e) => setFormData({...formData, mensaje: e.target.value})}
            rows="3"
            className="hero__form-textarea"
          />
        </div>
        
        {/* Bot protection: Honeypot field */}
        <input
          type="text"
          name="honeypot"
          value={formData.honeypot}
          onChange={(e) => setFormData({...formData, honeypot: e.target.value})}
          style={{ display: 'none' }}
          tabIndex="-1"
          autoComplete="off"
          aria-hidden="true"
        />
        
        <button 
          type="submit" 
          className="hero__form-submit"
          disabled={enviando}
        >
          {enviando ? (
            'Enviando...'
          ) : (
            <>
              Enviar <Send size={18} />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

// Componente para el contador de estadísticas
const StatCounter = ({ end, suffix = '', label }) => {
  const { count, ref } = useCountUp(end, 2000);
  return (
    <div className="hero__stat" ref={ref}>
      <span className="hero__stat-number">{count}{suffix}</span>
      <span className="hero__stat-label">{label}</span>
    </div>
  );
};

const Home = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [servicesRef, servicesVisible] = useScrollAnimation();
  const [aboutRef, aboutVisible] = useScrollAnimation();
  const [whyUsRef, whyUsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Artelaser3D",
    "url": "https://artelaser3d.es",
    "description": "Expertos en grabado láser e impresión 3D en Asturias",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://artelaser3d.es/buscar?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const services = [
    {
      icon: '/icons/laser_icono.png',
      title: 'Grabado Láser',
      description: 'Precisión milimétrica en madera, acrílico, cuero, metal y más. Personalización de regalos, señalética y productos corporativos.',
      link: '/grabado-laser-asturias'
    },
    {
      icon: '/icons/impresora_3d_icono.png',
      title: 'Impresión 3D',
      description: 'Prototipos funcionales, piezas técnicas, decoración y productos personalizados con las mejores tecnologías FDM y resina.',
      link: '/impresion-3d-asturias'
    },
    {
      icon: '/icons/regalo_icono.png',
      title: 'Regalos Personalizados',
      description: 'Ideas únicas para eventos, empresas y momentos especiales. Desde unidades hasta pequeñas series.',
      link: '/regalos-personalizados-asturias'
    },
    {
      icon: '/icons/produccion_en_serie_icono.png',
      title: 'Producción en Serie',
      description: 'Fabricación de pequeñas y medianas series para empresas. Calidad constante y plazos competitivos.',
      link: '/servicios'
    }
  ];

  const benefits = [
    {
      icon: <Zap size={28} />,
      title: 'Entrega Rápida',
      description: 'Plazos de 24-72h para proyectos urgentes'
    },
    {
      icon: <Target size={28} />,
      title: 'Precisión Total',
      description: 'Tecnología de última generación'
    },
    {
      icon: <Gem size={28} />,
      title: 'Calidad Premium',
      description: 'Materiales de primera calidad'
    },
    {
      icon: <Users size={28} />,
      title: 'Asesoramiento',
      description: 'Te guiamos en cada paso'
    }
  ];

  return (
    <>
      <SEO 
        title="Inicio"
        description="Artelaser3D - Expertos en grabado láser e impresión 3D en Asturias. Servicios personalizados para particulares y empresas con envío a toda España."
        canonicalUrl="https://artelaser3d.es"
        schema={schema}
      />

      {/* Hero Section */}
      <section 
        ref={heroRef}
        className={`hero ${heroVisible ? 'hero--visible' : ''}`}
        aria-labelledby="hero-title"
      >
        <div className="hero__background">
          <div className="hero__gradient"></div>
          <div className="hero__pattern"></div>
        </div>
        
        <div className="hero__container">
          <div className="hero__content">
            <h1 id="hero-title" className="hero__title">
              Transformamos tus ideas en
              <span className="hero__title-accent"> realidad digital</span>
            </h1>
            
            <p className="hero__description">
              Expertos en <strong>grabado láser</strong> e <strong>impresión 3D</strong> en Asturias. 
              Ofrecemos soluciones personalizadas para particulares y empresas 
              con la máxima calidad y precisión. Servicio en toda España.
            </p>
            
            <div className="hero__cta">
              <Link to="/presupuesto" className="hero__btn hero__btn--primary">
                Solicitar Presupuesto
                <svg className="hero__btn-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
              <Link to="/servicios" className="hero__btn hero__btn--secondary">
                Ver Servicios
              </Link>
            </div>
            
            <div className="hero__stats">
              <StatCounter end={500} suffix="+" label="Proyectos realizados" />
              <StatCounter end={98} suffix="%" label="Clientes satisfechos" />
              <StatCounter end={24} suffix="h" label="Respuesta garantizada" />
            </div>
          </div>
          
          <div className="hero__form-wrapper">
            <QuickContactForm />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        ref={servicesRef}
        className={`section section--services ${servicesVisible ? 'section--visible' : ''}`}
        aria-labelledby="services-title"
      >
        <div className="section__container">
          <header className="section__header">
            <span className="section__badge">Nuestros Servicios</span>
            <h2 id="services-title" className="section__title">
              Soluciones completas para tus proyectos
            </h2>
            <p className="section__description">
              Desde el diseño hasta la entrega final, te acompañamos en cada paso 
              para garantizar resultados excepcionales.
            </p>
          </header>

          <div className="services__grid">
            {services.map((service, index) => (
              <article key={index} className="service-card">
                <div className="service-card__icon">
                  <img src={service.icon} alt={service.title} className="service-card__icon-img" />
                </div>
                <h3 className="service-card__title">
                  <Link to={service.link}>{service.title}</Link>
                </h3>
                <p className="service-card__description">{service.description}</p>
                <Link to={service.link} className="service-card__link">
                  Saber más
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </Link>
              </article>
            ))}
          </div>

          <div className="section__cta">
            <Link to="/servicios" className="btn btn--primary btn--lg">
              Ver todos los servicios
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section 
        ref={aboutRef}
        className={`section section--about ${aboutVisible ? 'section--visible' : ''}`}
        aria-labelledby="about-title"
      >
        <div className="section__container">
          <div className="about__grid">
            <div className="about__content">
              <span className="section__badge">Sobre Nosotros</span>
              
              <h2 id="about-title" className="section__title">
                Dos especialistas, una visión:
                <span className="text-primary"> la excelencia</span>
              </h2>
              
              <p className="about__text">
                Artelaser3D nace de la unión de dos apasionados: un experto en <strong>grabado láser</strong> con años de experiencia en personalización y un especialista en <strong>impresión 3D</strong> con formación en diseño industrial y prototipado.
              </p>
              
              <p className="about__text">
                Juntos combinamos creatividad y tecnología para ofrecer soluciones 
                únicas. Trabajamos principalmente en <strong>Asturias</strong>, pero 
                nuestros servicios llegan a toda <strong>España</strong> gracias a 
                nuestra red de envíos.
              </p>

              <div className="about__features">
                <div className="about__feature">
                  <div className="about__feature-icon-wrapper">
                    <MapPin className="about__feature-icon" size={24} />
                  </div>
                  <div>
                    <strong>Base en Asturias</strong>
                    <span>Servicio local de confianza</span>
                  </div>
                </div>
                <div className="about__feature">
                  <div className="about__feature-icon-wrapper">
                    <Truck className="about__feature-icon" size={24} />
                  </div>
                  <div>
                    <strong>Envíos a toda España</strong>
                    <span>Rápido y seguro</span>
                  </div>
                </div>
              </div>

              <Link to="/quienes-somos" className="btn btn--outline">
                Conocer al equipo
              </Link>
            </div>

            <div className="about__visual">
              <div className="about__image-stack">
                <div className="about__image about__image--primary">
                  <img
                    src="/images/pexels-optlasers-7254457.jpg"
                    alt="Fundadores de Artelaser3D"
                    className="about__image-img"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section 
        ref={whyUsRef}
        className={`section section--why-us ${whyUsVisible ? 'section--visible' : ''}`}
        aria-labelledby="why-us-title"
      >
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">¿Por qué elegirnos?</span>
            
            <h2 id="why-us-title" className="section__title">
              Tu proyecto en las mejores manos
            </h2>
          </header>

          <div className="why-us__grid">
            {benefits.map((benefit, index) => (
              <article key={index} className="benefit-card">
                <div className="benefit-card__icon-wrapper">
                  <div className="benefit-card__icon">{benefit.icon}</div>
                </div>
                <h3 className="benefit-card__title">{benefit.title}</h3>
                <p className="benefit-card__description">{benefit.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        ref={ctaRef}
        className={`section section--cta ${ctaVisible ? 'section--visible' : ''}`}
        aria-labelledby="cta-title"
      >
        <div className="cta__content">
          <h2 id="cta-title" className="cta__title">
            ¿Tienes un proyecto en mente?
          </h2>
          
          <p className="cta__description">
            Solicita tu presupuesto sin compromiso. Te responderemos en menos de 24 horas 
            con una propuesta personalizada para tu proyecto.
          </p>

          <div className="cta__buttons">
            <Link to="/presupuesto" className="btn btn--primary btn--lg">
              Solicitar Presupuesto Gratis
            </Link>
            <Link to="/contacto" className="btn btn--outline-white btn--lg">
              Contactar
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
